import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useApp } from '../context/AppContext';
import { Badge } from '../components/Badge';
import { StatCard } from '../components/StatCard';
import { formatDate } from '../utils/format';
import {
  DEMO_AUDIENCES,
  DEMO_FEATURED_COMMS,
} from '../data/commsNotificationsDemo';
import {
  applyRrhhTemplateVars,
  RRHH_EMAIL_DEMO_VARS,
  RRHH_TEMPLATE_DEFAULTS,
  TEMPLATE_KEY_TO_EMAIL_TYPE,
} from '../data/rrhhEmailTemplates';
import { notifyCommsPublished } from '../services/appNotifications';
import {
  addCustomTemplate,
  duplicateTemplateRow,
  getMergedTemplatesList,
  removeCustomTemplate,
  subscribeTemplates,
  updateCustomTemplate,
  updateTemplateOverride,
} from '../services/commsTemplatesStorage';

const TABS = ['bulletins', 'emails', 'templates', 'history', 'audiences'];
const CATEGORIES = ['general', 'rrhh', 'compliance', 'benefits', 'urgent'];
const STATUSES = ['draft', 'published', 'archived'];
const AUDIENCES = ['all', 'managers', 'employees'];
const EMAIL_TYPES = [
  'welcome',
  'docs_pending',
  'doc_expired',
  'leave_approved',
  'leave_rejected',
  'expense_approved',
  'expense_rejected',
  'eval_pending',
  'training_assigned',
  'climate_survey',
  'general_comms',
];

const EMAIL_TYPE_DEFAULT_TPL = {
  welcome: 'tpl_welcome',
  docs_pending: 'tpl_docs_pending',
  doc_expired: 'tpl_doc_expired',
  leave_approved: 'tpl_leave_ok',
  leave_rejected: 'tpl_leave_rej',
  expense_approved: 'tpl_exp_ok',
  expense_rejected: 'tpl_exp_rej',
  eval_pending: 'tpl_eval_pending',
  training_assigned: 'tpl_training',
  climate_survey: 'tpl_climate_survey',
  general_comms: 'tpl_urgent_comms',
};

const emptyForm = {
  title: '',
  category: 'general',
  audience: 'all',
  status: 'draft',
  summary: '',
  body: '',
  author_name: 'RRHH Demo',
  publishToFeed: false,
};

const emptyCompose = {
  to_email: '',
  to_name: '',
  subject: '',
  emailType: 'welcome',
  templateKey: 'tpl_welcome',
  htmlBody: '',
  textBody: '',
  triggeredBy: 'rrhh_console',
};

const RECIPIENT_DEMO = { all: 248, managers: 42, employees: 196 };

const emptyTplForm = {
  id: null,
  key: '',
  isCustom: false,
  displayName: '',
  category: 'general',
  channel: 'email',
  status: 'active',
  vars: '{{employee_name}}',
  previewText: '',
};

const TEMPLATE_EDIT_CATEGORIES = [
  { id: 'welcome', labelKey: 'communications.tplCatWelcome' },
  { id: 'receipt', labelKey: 'communications.tplCatReceipt' },
  { id: 'leave', labelKey: 'communications.tplCatLeave' },
  { id: 'training', labelKey: 'communications.tplCatTraining' },
  { id: 'climate', labelKey: 'communications.tplCatClimate' },
  { id: 'general', labelKey: 'communications.tplCatGeneral' },
  { id: 'internal', labelKey: 'communications.tplCatInternal' },
];

function catVariant(c) {
  const map = {
    general: 'cat_general',
    rrhh: 'cat_rrhh',
    compliance: 'cat_compliance',
    benefits: 'cat_benefits',
    urgent: 'cat_urgent',
  };
  return map[c] || 'cat_general';
}

function audVariant(a) {
  const map = { all: 'aud_all', managers: 'aud_managers', employees: 'aud_employees' };
  return map[a] || 'aud_all';
}

function statusVariant(s) {
  if (s === 'published') return 'approved';
  if (s === 'archived') return 'cancelled';
  return 'draft';
}

function emailStatusVariant(s) {
  if (s === 'sent' || s === 'sent_sim') return 'approved';
  if (s === 'failed' || s === 'failed_sim') return 'rejected';
  if (s === 'simulated') return 'pending';
  if (s === 'queued') return 'pending';
  if (s === 'prepared') return 'manager_approved';
  return 'draft';
}

export function Communications() {
  const { t, locale } = useApp();
  const [tab, setTab] = useState('bulletins');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [cat, setCat] = useState('');
  const [st, setSt] = useState('');
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [viewRow, setViewRow] = useState(null);
  const [rrhhConfig, setRrhhConfig] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [compose, setCompose] = useState(emptyCompose);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailFeedback, setEmailFeedback] = useState(null);
  const [realSendAck, setRealSendAck] = useState(false);
  const emailFormSeeded = useRef(false);
  const [templateRows, setTemplateRows] = useState(() => getMergedTemplatesList());
  const [tplModal, setTplModal] = useState(false);
  const [tplForm, setTplForm] = useState(emptyTplForm);
  const [tplPreviewRow, setTplPreviewRow] = useState(null);
  const [tplToast, setTplToast] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingCommIdRef = useRef(null);
  const [highlightCommId, setHighlightCommId] = useState(null);
  const [commNavToast, setCommNavToast] = useState(null);

  useEffect(() => {
    setTemplateRows(getMergedTemplatesList());
    return subscribeTemplates(() => setTemplateRows(getMergedTemplatesList()));
  }, []);

  useEffect(() => {
    if (!tplToast) return undefined;
    const id = window.setTimeout(() => setTplToast(null), 2600);
    return () => window.clearTimeout(id);
  }, [tplToast]);

  useEffect(() => {
    if (tab === 'emails' && !emailFormSeeded.current) {
      emailFormSeeded.current = true;
      const raw = RRHH_TEMPLATE_DEFAULTS.tpl_welcome;
      if (raw) {
        setCompose({
          ...emptyCompose,
          templateKey: 'tpl_welcome',
          emailType: 'welcome',
          subject: applyRrhhTemplateVars(raw.subject, RRHH_EMAIL_DEMO_VARS),
          htmlBody: applyRrhhTemplateVars(raw.html, RRHH_EMAIL_DEMO_VARS),
          textBody: applyRrhhTemplateVars(raw.text, RRHH_EMAIL_DEMO_VARS),
        });
      }
    }
  }, [tab]);

  const loadEmailLogs = useCallback(async () => {
    try {
      const { data } = await api.get('/api/rrhh-emails/logs', { params: { limit: 200 } });
      setEmailLogs(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setEmailLogs([]);
    }
  }, []);

  const loadRrhhConfig = useCallback(async () => {
    try {
      const { data } = await api.get('/api/rrhh-emails/config');
      setRrhhConfig(data && typeof data === 'object' ? data : null);
    } catch (e) {
      console.error(e);
      setRrhhConfig(null);
    }
  }, []);

  useEffect(() => {
    if (tab === 'emails' || tab === 'history') {
      loadRrhhConfig();
      loadEmailLogs();
    }
  }, [tab, loadRrhhConfig, loadEmailLogs]);

  useEffect(() => {
    const id = searchParams.get('commId');
    if (!id) return;
    pendingCommIdRef.current = id;
    setTab('bulletins');
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.delete('commId');
        return n;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!commNavToast) return undefined;
    const z = window.setTimeout(() => setCommNavToast(null), 4200);
    return () => window.clearTimeout(z);
  }, [commNavToast]);

  useEffect(() => {
    if (!highlightCommId) return undefined;
    const z = window.setTimeout(() => setHighlightCommId(null), 4500);
    return () => window.clearTimeout(z);
  }, [highlightCommId]);

  useEffect(() => {
    const id = pendingCommIdRef.current;
    if (!id) return;
    if (loading) return;
    const found = rows.some((r) => r.id === id);
    if (found) {
      setHighlightCommId(id);
      pendingCommIdRef.current = null;
      requestAnimationFrame(() => {
        document.getElementById(`comm-card-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }
    pendingCommIdRef.current = null;
    setCommNavToast(t('communications.commNotFoundToast'));
  }, [rows, loading, t]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(false);
    try {
      const params = {};
      if (cat) params.category = cat;
      if (st) params.status = st;
      const { data } = await api.get('/api/communications', { params });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setErr(true);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [cat, st]);

  useEffect(() => {
    load();
  }, [load]);

  const publishedCount = useMemo(() => rows.filter((c) => c.status === 'published').length, [rows]);
  const preparedEmailCount = useMemo(
    () => emailLogs.filter((e) => e.status === 'prepared' || e.status === 'draft').length,
    [emailLogs]
  );
  const templatesActiveCount = useMemo(() => templateRows.filter((x) => x.status === 'active').length, [templateRows]);
  const pendingMsgsCount = useMemo(
    () => emailLogs.filter((e) => ['draft', 'prepared'].includes(e.status)).length,
    [emailLogs]
  );

  async function create() {
    try {
      const { publishToFeed, ...payload } = form;
      const { data } = await api.post('/api/communications', payload);
      if (form.status === 'published' && form.publishToFeed && data?.id) {
        await api.put(`/api/communications/${data.id}`, { publish_to_feed: true });
      }
      setModal(false);
      if (form.status === 'published') {
        notifyCommsPublished(t, form.title, data?.id);
      }
      setForm(emptyForm);
      await load();
    } catch (e) {
      console.error(e);
    }
  }

  async function saveEdit() {
    if (!editingId) return;
    const prev = rows.find((r) => r.id === editingId);
    try {
      await api.put(`/api/communications/${editingId}`, {
        title: form.title,
        category: form.category,
        audience: form.audience,
        status: form.status,
        body: form.body,
        summary: form.summary,
        author_name: form.author_name,
        ...(form.status === 'published' && form.publishToFeed ? { publish_to_feed: true } : {}),
      });
      if (form.status === 'published' && prev && prev.status !== 'published') {
        notifyCommsPublished(t, form.title, editingId);
      }
      setModal(false);
      setEditingId(null);
      setForm(emptyForm);
      await load();
    } catch (e) {
      console.error(e);
    }
  }

  async function setStatus(id, status) {
    const prev = rows.find((r) => r.id === id);
    try {
      await api.put(`/api/communications/${id}`, { status });
      await load();
      if (status === 'published' && prev && prev.status !== 'published') {
        notifyCommsPublished(t, prev.title || '—', id);
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function duplicateComm(c) {
    try {
      await api.post('/api/communications', {
        title: `${c.title || t('communications.emailUntitled')}${locale === 'en' ? ' (copy)' : ' (copia)'}`,
        category: c.category || 'general',
        audience: c.audience || 'all',
        status: 'draft',
        summary: c.summary || '',
        body: c.body || '',
        author_name: c.author_name || 'RRHH Demo',
      });
      await load();
    } catch (e) {
      console.error(e);
    }
  }

  async function remove(id) {
    if (!window.confirm(t('communications.confirmDelete'))) return;
    try {
      await api.delete(`/api/communications/${id}`);
      await load();
    } catch (e) {
      console.error(e);
    }
  }

  function openCreate() {
    setEditingId(null);
    setForm(emptyForm);
    setModal(true);
  }

  function openEdit(c) {
    setEditingId(c.id);
    setForm({
      title: c.title || '',
      category: c.category || 'general',
      audience: c.audience || 'all',
      status: c.status || 'draft',
      summary: c.summary || '',
      body: c.body || '',
      author_name: c.author_name || 'RRHH Demo',
      publishToFeed: false,
    });
    setModal(true);
  }

  function closeModal() {
    setModal(false);
    setEditingId(null);
    setForm(emptyForm);
  }

  function applyTemplateFromKey(tplKey) {
    const raw = RRHH_TEMPLATE_DEFAULTS[tplKey];
    if (!raw) return;
    const subj = applyRrhhTemplateVars(raw.subject, RRHH_EMAIL_DEMO_VARS);
    const html = applyRrhhTemplateVars(raw.html, RRHH_EMAIL_DEMO_VARS);
    const txt = applyRrhhTemplateVars(raw.text, RRHH_EMAIL_DEMO_VARS);
    const mappedType = TEMPLATE_KEY_TO_EMAIL_TYPE[tplKey] || compose.emailType;
    setCompose((c) => ({
      ...c,
      templateKey: tplKey,
      emailType: mappedType,
      subject: subj,
      htmlBody: html,
      textBody: txt,
    }));
  }

  async function handlePrepareEmail() {
    setEmailBusy(true);
    setEmailFeedback(null);
    try {
      const { data } = await api.post('/api/rrhh-emails/prepare', {
        to_email: compose.to_email.trim(),
        to_name: compose.to_name.trim(),
        subject: compose.subject.trim(),
        html_body: compose.htmlBody,
        text_body: compose.textBody,
        template_key: compose.templateKey,
        email_type: compose.emailType,
        metadata: { source: 'communications_ui' },
        triggered_by: compose.triggeredBy,
      });
      setEmailFeedback(data?.message || t('communications.emailPrepareOk'));
      await loadEmailLogs();
    } catch (e) {
      console.error(e);
      setEmailFeedback(t('communications.emailSoftWarning'));
    } finally {
      setEmailBusy(false);
    }
  }

  async function handleSendEmail() {
    const cfg = rrhhConfig;
    const isReal = cfg && cfg.status === 'ready' && cfg.mode === 'real';
    if (isReal && !realSendAck) {
      setEmailFeedback(t('communications.emailRealAckMissing'));
      return;
    }
    setEmailBusy(true);
    setEmailFeedback(null);
    try {
      const { data } = await api.post('/api/rrhh-emails/send', {
        to_email: compose.to_email.trim(),
        to_name: compose.to_name.trim(),
        subject: compose.subject.trim(),
        html_body: compose.htmlBody || '<p></p>',
        text_body: compose.textBody || '',
        template_key: compose.templateKey,
        email_type: compose.emailType,
        metadata: { source: 'communications_ui' },
        triggered_by: compose.triggeredBy,
      });
      setEmailFeedback(data?.message || t('communications.emailSendOk'));
      setRealSendAck(false);
      await loadEmailLogs();
    } catch (e) {
      console.error(e);
      setEmailFeedback(t('communications.emailSoftWarning'));
    } finally {
      setEmailBusy(false);
    }
  }

  function tplTitleRow(tpl) {
    if (tpl.displayName) return tpl.displayName;
    const ik = `communications.template.${tpl.key}`;
    const tx = t(ik);
    return tx === ik ? tpl.key : tx;
  }

  function tplPreviewContent(tpl) {
    if (tpl.previewText) return tpl.previewText;
    const pk = `communications.templatePreview.${tpl.key}`;
    const tx = t(pk);
    return tx === pk ? '' : tx;
  }

  function openTplCreate() {
    setTplForm({
      id: null,
      key: '',
      isCustom: true,
      displayName: '',
      category: 'general',
      channel: 'email',
      status: 'draft',
      vars: '{{employee_name}}',
      previewText: '',
    });
    setTplModal(true);
  }

  function openTplEdit(tpl) {
    let previewText = tpl.previewText || '';
    if (!tpl.isCustom && !previewText) {
      const pk = `communications.templatePreview.${tpl.key}`;
      const tx = t(pk);
      if (tx !== pk) previewText = tx;
    }
    setTplForm({
      id: tpl.isCustom ? tpl.id : null,
      key: tpl.key,
      isCustom: !!tpl.isCustom,
      displayName: tpl.displayName || '',
      category: tpl.category || 'general',
      channel: tpl.channel || 'email',
      status: tpl.status || 'active',
      vars: tpl.vars || '',
      previewText,
    });
    setTplModal(true);
  }

  function saveTpl() {
    const name = tplForm.displayName.trim() || tplForm.key || 'template';
    if (tplForm.isCustom) {
      if (tplForm.id) {
        updateCustomTemplate(tplForm.id, {
          displayName: name,
          category: tplForm.category,
          channel: tplForm.channel,
          status: tplForm.status,
          vars: tplForm.vars,
          previewText: tplForm.previewText,
        });
      } else {
        addCustomTemplate({
          displayName: name,
          category: tplForm.category,
          channel: tplForm.channel,
          status: tplForm.status,
          vars: tplForm.vars,
          previewText: tplForm.previewText,
        });
      }
    } else {
      updateTemplateOverride(tplForm.key, {
        displayName: tplForm.displayName.trim() || undefined,
        category: tplForm.category,
        channel: tplForm.channel,
        status: tplForm.status,
        vars: tplForm.vars,
        previewText: tplForm.previewText,
      });
    }
    setTplModal(false);
    setTplToast(t('communications.tplToastSaved'));
    setTemplateRows(getMergedTemplatesList());
  }

  function deleteTplRow(tpl) {
    if (!window.confirm(t('communications.tplConfirmDelete'))) return;
    if (tpl.isCustom) removeCustomTemplate(tpl.id);
    else updateTemplateOverride(tpl.key, { hidden: true });
    setTplToast(t('communications.tplToastSaved'));
    setTemplateRows(getMergedTemplatesList());
  }

  function tplChannelLabel(ch) {
    if (ch === 'portal') return t('communications.chPortal');
    if (ch === 'internal') return t('communications.chInternal');
    return t('communications.chEmail');
  }

  return (
    <div className="page-stack communications-page comm-page--v21">
      {tplToast ? <div className="app-toast app-toast--ok">{tplToast}</div> : null}
      {commNavToast ? <div className="app-toast app-toast--ok">{commNavToast}</div> : null}
      <header className="comm-hero panel panel--elevated">
        <h1 className="comm-hero__title">{t('communications.title')}</h1>
        <p className="comm-hero__lead">{t('communications.subtitle')}</p>
        <div className="comm-kpi-row grid-stats">
          <StatCard icon="✉" label={t('communications.kpiPublished')} value={publishedCount} />
          <StatCard icon="◆" label={t('communications.kpiEmailsPrepared')} value={preparedEmailCount} tone="accent" />
          <StatCard icon="▤" label={t('communications.kpiTemplatesActive')} value={templatesActiveCount} />
          <StatCard icon="⏱" label={t('communications.kpiPendingMsgs')} value={pendingMsgsCount} tone="warning" />
          <StatCard
            icon="◎"
            label={t('communications.kpiReadRate')}
            value={t('communications.kpiReadRateValue')}
            hint={t('communications.kpiReadRateHint')}
          />
        </div>
      </header>

      <section className="panel panel--elevated comm-premium-strip" aria-label={t('communications.premiumStripTitle')}>
        <h2 className="panel__title">{t('communications.premiumStripTitle')}</h2>
        <p className="panel__muted text-sm">{t('communications.premiumStripLead')}</p>
        <div className="comm-premium-strip__chips">
          <span className="dash-pill dash-pill--muted">{t('communications.chPortal')}</span>
          <span className="dash-pill dash-pill--muted">{t('communications.chInternal')}</span>
          <span className="dash-pill dash-pill--muted">{t('communications.chEmail')}</span>
          <span className="dash-pill dash-pill--ok">{t('communications.audience.all')}</span>
          <span className="dash-pill dash-pill--ok">{t('communications.audience.managers')}</span>
        </div>
      </section>

      <div className="banner banner--info comm-mode-banner">{t('communications.modeBanner')}</div>

      <div className="comm-tabs panel panel--elevated" role="tablist" aria-label={t('communications.tabsAria')}>
        {TABS.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={`comm-tab${tab === id ? ' is-active' : ''}`}
            onClick={() => setTab(id)}
          >
            {t(`communications.tab_${id}`)}
          </button>
        ))}
      </div>

      {err && <div className="banner banner--info">{t('common.error')}</div>}

      {tab === 'bulletins' && (
        <>
          <section className="banner banner--muted comm-see-banner">
            <h2 className="panel__title comm-see-banner__title">{t('communications.whereSeeBannerTitle')}</h2>
            <p className="panel__muted text-sm">{t('communications.whereSeeBannerLead')}</p>
          </section>
          <section className="banner banner--info comm-internal-banner">
            <p className="text-sm" style={{ margin: 0 }}>
              {t('communications.internalPublishBanner')}
            </p>
          </section>
          {highlightCommId ? (
            <section className="banner banner--muted comm-selected-banner" role="status">
              <p className="text-sm" style={{ margin: 0 }}>
                {t('communications.commSelectedBanner')}
              </p>
            </section>
          ) : null}
          <section className="comm-featured panel panel--elevated">
            <h2 className="panel__title">{t('communications.featuredTitle')}</h2>
            <div className="comm-featured-grid">
              {DEMO_FEATURED_COMMS.map((fc) => (
                <article key={fc.id} className="comm-featured-card">
                  <div className="comm-featured-card__badges">
                    <Badge variant={catVariant(fc.category)}>{t(`communications.category.${fc.category}`)}</Badge>
                    <Badge variant={audVariant(fc.audience)}>{t(`communications.audience.${fc.audience}`)}</Badge>
                  </div>
                  <h3 className="comm-featured-card__title">{fc.title}</h3>
                  <p className="comm-featured-card__summary">{fc.summary}</p>
                  <p className="comm-featured-card__meta">
                    {fc.author} · {formatDate(fc.date)}
                  </p>
                  <div className="comm-featured-card__actions">
                    <button type="button" className="btn btn--sm btn--ghost" onClick={() => setViewRow({ ...fc, demo: true })}>
                      {t('communications.actionView')}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <div className="toolbar toolbar--card">
            <div className="form-row form-row--filters">
              <label className="field field--inline">
                <span>{t('communications.filterCategory')}</span>
                <select className="input" value={cat} onChange={(e) => setCat(e.target.value)}>
                  <option value="">{t('communications.all')}</option>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {t(`communications.category.${c}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field field--inline">
                <span>{t('communications.filterStatus')}</span>
                <select className="input" value={st} onChange={(e) => setSt(e.target.value)}>
                  <option value="">{t('communications.all')}</option>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {t(`communications.status.${s}`)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="toolbar__grow" />
            <button type="button" className="btn btn--primary" onClick={openCreate}>
              {t('communications.new')}
            </button>
          </div>

          {loading && rows.length === 0 ? (
            <div className="empty-state empty-state--boxed">{t('common.loading')}</div>
          ) : rows.length === 0 ? (
            <div className="empty-state empty-state--boxed">{t('communications.empty')}</div>
          ) : (
            <div className="comm-feed">
              {rows.map((c) => (
                <article
                  id={`comm-card-${c.id}`}
                  key={c.id}
                  className={`panel panel--elevated comm-card comm-card--${c.status}${
                    highlightCommId === c.id ? ' comm-card--highlighted' : ''
                  }`}
                >
                  <div className="comm-card__head">
                    <h3 className="comm-card__title">{c.title}</h3>
                    <div className="comm-card__badges">
                      <Badge variant={catVariant(c.category)}>{t(`communications.category.${c.category}`)}</Badge>
                      <Badge variant={audVariant(c.audience)}>{t(`communications.audience.${c.audience}`)}</Badge>
                      <Badge variant={statusVariant(c.status)}>{t(`communications.status.${c.status}`)}</Badge>
                    </div>
                  </div>
                  <p className="comm-card__meta">
                    {c.author_name} · {c.published_at ? formatDate(c.published_at) : t('communications.notPublished')}
                  </p>
                  <p className="comm-card__summary">{c.summary || c.body?.slice(0, 220) || '—'}</p>
                  <p className="panel__muted text-sm">
                    {t('communications.recipientEstPrefix')} ~{RECIPIENT_DEMO[c.audience] ?? '—'}
                  </p>
                  <div className="comm-card__actions comm-card__actions--wrap">
                    <button type="button" className="btn btn--sm btn--ghost" onClick={() => setViewRow(c)}>
                      {t('communications.actionView')}
                    </button>
                    <button type="button" className="btn btn--sm btn--secondary" onClick={() => openEdit(c)}>
                      {t('communications.actionEdit')}
                    </button>
                    <button type="button" className="btn btn--sm btn--secondary" onClick={() => duplicateComm(c)}>
                      {t('communications.actionDuplicate')}
                    </button>
                    {c.status === 'draft' && (
                      <button type="button" className="btn btn--sm btn--primary" onClick={() => setStatus(c.id, 'published')}>
                        {t('communications.actionPublish')}
                      </button>
                    )}
                    {c.status === 'published' && (
                      <button type="button" className="btn btn--sm btn--secondary" onClick={() => setStatus(c.id, 'archived')}>
                        {t('communications.actionArchive')}
                      </button>
                    )}
                    {c.status === 'archived' && (
                      <button type="button" className="btn btn--sm btn--ghost" onClick={() => setStatus(c.id, 'draft')}>
                        {t('communications.unarchive')}
                      </button>
                    )}
                    <button type="button" className="btn btn--sm btn--ghost" onClick={() => remove(c.id)}>
                      {t('communications.delete')}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'emails' && (
        <div className="comm-emails-stack">
          <section className="comm-emails panel panel--elevated">
            <div className="comm-emails__head">
              <div>
                <h2 className="panel__title">{t('communications.emailPanelTitle')}</h2>
                <p className="panel__muted">{t('communications.emailPanelLeadResend')}</p>
              </div>
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => { loadRrhhConfig(); loadEmailLogs(); }}>
                {t('communications.emailRefresh')}
              </button>
            </div>
            {rrhhConfig && (
              <div className={`comm-provider-pill comm-provider-pill--${rrhhConfig.status || 'unknown'}`}>
                <span className="comm-provider-pill__label">{t('communications.emailProviderState')}</span>
                <strong>{t(`communications.emailConfigStatus_${rrhhConfig.status}`)}</strong>
                <span className="panel__muted text-sm">
                  · {t('communications.emailModeLabel')}: {t(rrhhConfig.mode === 'real' ? 'communications.emailModeReal' : 'communications.emailModeSimulation')}
                </span>
              </div>
            )}
            {rrhhConfig && rrhhConfig.status !== 'ready' && (
              <div className="banner banner--muted">
                {rrhhConfig.status === 'simulation' || rrhhConfig.mode === 'simulation'
                  ? t('communications.emailSimBanner')
                  : rrhhConfig.status === 'disabled'
                    ? t('communications.emailDisabledBanner')
                    : rrhhConfig.status === 'missing_api_key'
                      ? t('communications.emailMissingKeyBanner')
                      : t('communications.emailGenericBanner')}
              </div>
            )}
            {rrhhConfig && rrhhConfig.status === 'ready' && (
              <div className="banner banner--info">{t('communications.emailRealBanner')}</div>
            )}
            <div className="comm-email-compose panel panel--elevated">
              <h3 className="panel__title">{t('communications.emailComposeTitle')}</h3>
              <div className="form-grid">
                <label className="field field--full">
                  <span>{t('communications.fieldToEmail')}</span>
                  <input
                    className="input"
                    type="email"
                    autoComplete="email"
                    value={compose.to_email}
                    onChange={(e) => setCompose({ ...compose, to_email: e.target.value })}
                  />
                </label>
                <label className="field field--full">
                  <span>{t('communications.fieldToName')}</span>
                  <input
                    className="input"
                    value={compose.to_name}
                    onChange={(e) => setCompose({ ...compose, to_name: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span>{t('communications.fieldEmailType')}</span>
                  <select
                    className="input"
                    value={compose.emailType}
                    onChange={(e) => {
                      const et = e.target.value;
                      const tpl = EMAIL_TYPE_DEFAULT_TPL[et] || 'tpl_welcome';
                      setCompose({ ...compose, emailType: et, templateKey: tpl });
                    }}
                  >
                    {EMAIL_TYPES.map((k) => (
                      <option key={k} value={k}>
                        {t(`communications.emailType.${k}`)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>{t('communications.fieldTemplate')}</span>
                  <select
                    className="input"
                    value={compose.templateKey}
                    onChange={(e) => applyTemplateFromKey(e.target.value)}
                  >
                    {templateRows.map((tpl) => (
                      <option key={tpl.key} value={tpl.key}>
                        {tplTitleRow(tpl)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field field--full">
                  <span>{t('communications.fieldSubject')}</span>
                  <input className="input" value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })} />
                </label>
                <label className="field field--full">
                  <span>{t('communications.fieldHtmlBody')}</span>
                  <textarea
                    className="input input--textarea"
                    rows={6}
                    value={compose.htmlBody}
                    onChange={(e) => setCompose({ ...compose, htmlBody: e.target.value })}
                  />
                </label>
                <label className="field field--full">
                  <span>{t('communications.fieldTextBody')}</span>
                  <textarea
                    className="input input--textarea"
                    rows={3}
                    value={compose.textBody}
                    onChange={(e) => setCompose({ ...compose, textBody: e.target.value })}
                  />
                </label>
              </div>
              <div className="comm-email-preview panel panel--elevated">
                <h4 className="comm-email-preview__title">{t('communications.emailPreviewTitle')}</h4>
                <div className="comm-email-preview__box" dangerouslySetInnerHTML={{ __html: compose.htmlBody || '<p></p>' }} />
              </div>
              {rrhhConfig && rrhhConfig.status === 'ready' && rrhhConfig.mode === 'real' && (
                <label className="field field--full comm-email-ack">
                  <input type="checkbox" checked={realSendAck} onChange={(e) => setRealSendAck(e.target.checked)} />
                  <span>{t('communications.emailRealAck')}</span>
                </label>
              )}
              {emailFeedback && <div className="banner banner--info comm-email-feedback">{emailFeedback}</div>}
              <div className="comm-email-actions">
                <button type="button" className="btn btn--secondary" disabled={emailBusy} onClick={() => applyTemplateFromKey(compose.templateKey)}>
                  {t('communications.emailReloadTemplate')}
                </button>
                <button type="button" className="btn btn--secondary" disabled={emailBusy} onClick={handlePrepareEmail}>
                  {t('communications.emailPrepareSave')}
                </button>
                <button type="button" className="btn btn--primary" disabled={emailBusy} onClick={handleSendEmail}>
                  {rrhhConfig && rrhhConfig.status === 'ready' && rrhhConfig.mode === 'real'
                    ? t('communications.emailSendReal')
                    : t('communications.emailSendSimulated')}
                </button>
              </div>
            </div>
          </section>
          <section className="panel panel--elevated comm-email-log-section">
            <h3 className="panel__title">{t('communications.emailRecentLogTitle')}</h3>
            <div className="table-wrap table-wrap--rounded">
              <table className="data-table comm-email-table">
                <thead>
                  <tr>
                    <th>{t('communications.emailColDate')}</th>
                    <th>{t('communications.emailColTo')}</th>
                    <th>{t('communications.fieldSubject')}</th>
                    <th>{t('communications.fieldEmailType')}</th>
                    <th>{t('communications.emailColStatus')}</th>
                    <th>{t('communications.emailColProvider')}</th>
                  </tr>
                </thead>
                <tbody>
                  {emailLogs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="panel__muted">
                        {t('communications.emailLogEmpty')}
                      </td>
                    </tr>
                  ) : (
                    emailLogs.slice(0, 50).map((row) => (
                      <tr key={row.id}>
                        <td className="mono">{formatDate(row.created_at)}</td>
                        <td>
                          {row.to_name ? `${row.to_name} · ` : ''}
                          {row.to_email}
                        </td>
                        <td>{row.subject}</td>
                        <td>{row.email_type ? t(`communications.emailType.${row.email_type}`) : '—'}</td>
                        <td>
                          <Badge variant={emailStatusVariant(row.status)}>{t(`communications.emailLogStatus.${row.status}`)}</Badge>
                        </td>
                        <td>{t(`communications.emailProvider.${row.provider}`)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {tab === 'templates' && (
        <div className="comm-templates">
          <div className="toolbar toolbar--card" style={{ marginBottom: '0.75rem' }}>
            <h2 className="panel__title" style={{ margin: 0 }}>
              {t('communications.tab_templates')}
            </h2>
            <div className="toolbar__grow" />
            <button type="button" className="btn btn--primary btn--sm" onClick={openTplCreate}>
              {t('communications.tplNew')}
            </button>
          </div>
          <section className="panel panel--elevated comm-variables-block">
            <h2 className="panel__title">{t('communications.variablesBlockTitle')}</h2>
            <p className="panel__muted">{t('communications.variablesBlockBody')}</p>
            <ul className="comm-variables-list">
              {String(t('communications.variablesList'))
                .split('|')
                .map((line) => (
                  <li key={line}>{line.trim()}</li>
                ))}
            </ul>
          </section>
          <div className="comm-template-grid">
            {templateRows.map((tpl) => {
              const catKey = ['general', 'rrhh', 'compliance', 'benefits', 'urgent'].includes(tpl.category) ? tpl.category : 'general';
              const pv = tplPreviewContent(tpl);
              return (
                <article key={`${tpl.key}-${tpl.id || 'd'}`} className="panel panel--elevated comm-template-card">
                  <div className="comm-template-card__head">
                    <h3 className="comm-template-card__title">{tplTitleRow(tpl)}</h3>
                    <Badge variant={catVariant(catKey)}>{tpl.category}</Badge>
                  </div>
                  <p className="panel__muted text-sm">
                    {t('communications.templateChannel')}: <strong>{tplChannelLabel(tpl.channel)}</strong> · {t('communications.templateStatus')}:{' '}
                    <strong>{tpl.status === 'active' ? t('communications.tplActive') : t('communications.tplDraft')}</strong>
                  </p>
                  <p className="panel__muted text-sm">
                    {t('communications.templateVars')}: <code className="comm-code">{tpl.vars}</code>
                  </p>
                  <p className="comm-template-preview">{pv || '—'}</p>
                  <p className="comm-template-card__foot mono text-sm">
                    {t('communications.templateEdited')}: {formatDate(tpl.updatedAt)}
                  </p>
                  <div className="comm-card__actions comm-card__actions--wrap">
                    <button type="button" className="btn btn--sm btn--ghost" onClick={() => setTplPreviewRow(tpl)}>
                      {t('communications.tplPreview')}
                    </button>
                    <button type="button" className="btn btn--sm btn--secondary" onClick={() => openTplEdit(tpl)}>
                      {t('communications.tplEdit')}
                    </button>
                    <button
                      type="button"
                      className="btn btn--sm btn--secondary"
                      onClick={() => {
                        duplicateTemplateRow(tpl);
                        setTplToast(t('communications.tplToastSaved'));
                        setTemplateRows(getMergedTemplatesList());
                      }}
                    >
                      {t('communications.tplDuplicate')}
                    </button>
                    <button type="button" className="btn btn--sm btn--ghost" onClick={() => deleteTplRow(tpl)}>
                      {t('communications.tplDelete')}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'history' && (
        <div className="comm-history panel panel--elevated">
          <h2 className="panel__title">{t('communications.historyTitle')}</h2>
          <p className="panel__muted">{t('communications.historyLeadResend')}</p>
          <div className="table-wrap table-wrap--rounded">
            <table className="data-table comm-history-table">
              <thead>
                <tr>
                  <th>{t('communications.emailColDate')}</th>
                  <th>{t('communications.historyTo')}</th>
                  <th>{t('communications.fieldSubject')}</th>
                  <th>{t('communications.fieldEmailType')}</th>
                  <th>{t('communications.emailColStatus')}</th>
                  <th>{t('communications.emailColProvider')}</th>
                  <th>{t('communications.historyActor')}</th>
                  <th>{t('communications.emailColError')}</th>
                </tr>
              </thead>
              <tbody>
                {emailLogs.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="panel__muted">
                      {t('communications.emailLogEmpty')}
                    </td>
                  </tr>
                ) : (
                  emailLogs.map((row) => (
                    <tr key={row.id}>
                      <td className="mono">{formatDate(row.created_at)}</td>
                      <td>
                        {row.to_name ? `${row.to_name} · ` : ''}
                        {row.to_email}
                      </td>
                      <td>{row.subject}</td>
                      <td>{row.email_type ? t(`communications.emailType.${row.email_type}`) : '—'}</td>
                      <td>
                        <Badge variant={emailStatusVariant(row.status)}>{t(`communications.emailLogStatus.${row.status}`)}</Badge>
                      </td>
                      <td>{t(`communications.emailProvider.${row.provider}`)}</td>
                      <td>{row.triggered_by || '—'}</td>
                      <td className="text-sm panel__muted">{row.error_message || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'audiences' && (
        <div className="comm-audiences">
          <p className="panel__muted comm-audiences__intro">{t('communications.audiencesIntro')}</p>
          <div className="comm-audience-grid">
            {DEMO_AUDIENCES.map((a) => (
              <article key={a.id} className="panel panel--elevated comm-audience-card">
                <h3 className="comm-audience-card__title">{t(`communications.audienceName.${a.key}`)}</h3>
                <p className="comm-audience-card__count">
                  ~{a.count} {t('communications.audienceCountSuffix')}
                </p>
                <p className="panel__muted text-sm">{t(`communications.${a.useKey}`)}</p>
                <p className="panel__muted text-sm">
                  {t('communications.audienceChannels')}: {t(`communications.${a.channelsKey}`)}
                </p>
                <Badge variant="approved">{t(`communications.${a.statusKey}`)}</Badge>
              </article>
            ))}
          </div>
        </div>
      )}

      {modal && (
        <div className="modal-backdrop" role="dialog" aria-modal>
          <div className="modal modal--wide">
            <h2 className="modal__title">{editingId ? t('communications.editModalTitle') : t('communications.modalTitle')}</h2>
            <div className="form-grid">
              <label className="field field--full">
                <span>{t('communications.fieldTitle')}</span>
                <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </label>
              <label className="field">
                <span>{t('communications.fieldCategory')}</span>
                <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {t(`communications.category.${c}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>{t('communications.fieldAudience')}</span>
                <select className="input" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}>
                  {AUDIENCES.map((a) => (
                    <option key={a} value={a}>
                      {t(`communications.audience.${a}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>{t('communications.fieldStatus')}</span>
                <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {t(`communications.status.${s}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field field--full">
                <span>{t('communications.fieldSummary')}</span>
                <input className="input" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
              </label>
              <label className="field field--full">
                <span>{t('communications.fieldBody')}</span>
                <textarea className="input input--textarea" rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
              </label>
              <label className="field field--full">
                <span>{t('communications.fieldAuthor')}</span>
                <input className="input" value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} />
              </label>
              <label className="field field--full comm-publish-feed">
                <span className="comm-publish-feed__row">
                  <input
                    type="checkbox"
                    checked={!!form.publishToFeed}
                    disabled={form.status !== 'published'}
                    onChange={(e) => setForm({ ...form, publishToFeed: e.target.checked })}
                  />
                  {t('communications.publishToFeed')}
                </span>
                <span className="panel__muted text-sm">{t('communications.publishToFeedHint')}</span>
              </label>
            </div>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={closeModal}>
                {t('employees.cancel')}
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={editingId ? saveEdit : create}
                disabled={!form.title.trim()}
              >
                {t('employees.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewRow && (
        <div className="modal-backdrop" role="dialog" aria-modal>
          <div className="modal modal--wide">
            <h2 className="modal__title">{t('communications.viewModalTitle')}</h2>
            <div className="comm-view-body">
              <p className="comm-view-title">{viewRow.title}</p>
              {!viewRow.demo && (
                <>
                  <div className="comm-card__badges" style={{ marginBottom: '0.75rem' }}>
                    <Badge variant={catVariant(viewRow.category)}>{t(`communications.category.${viewRow.category}`)}</Badge>
                    <Badge variant={audVariant(viewRow.audience)}>{t(`communications.audience.${viewRow.audience}`)}</Badge>
                    <Badge variant={statusVariant(viewRow.status)}>{t(`communications.status.${viewRow.status}`)}</Badge>
                  </div>
                  <p className="panel__muted">
                    {viewRow.author_name} · {viewRow.published_at ? formatDate(viewRow.published_at) : t('communications.notPublished')}
                  </p>
                  <p className="comm-view-summary">{viewRow.summary || '—'}</p>
                  <div className="comm-view-html">{viewRow.body || '—'}</div>
                  <ul className="comm-timeline" style={{ marginTop: '1rem', listStyle: 'disc', paddingLeft: '1.25rem' }}>
                    <li>
                      {t('communications.timelineCreated')}: {viewRow.created_at ? formatDate(viewRow.created_at) : '—'}
                    </li>
                    <li>
                      {t('communications.timelineUpdated')}: {viewRow.updated_at ? formatDate(viewRow.updated_at) : '—'}
                    </li>
                    <li>
                      {t('communications.timelinePublished')}: {viewRow.published_at ? formatDate(viewRow.published_at) : '—'}
                    </li>
                    <li>{t('communications.timelineViewed')}</li>
                  </ul>
                </>
              )}
              {viewRow.demo && (
                <>
                  <div className="comm-card__badges" style={{ marginBottom: '0.75rem' }}>
                    <Badge variant={catVariant(viewRow.category)}>{t(`communications.category.${viewRow.category}`)}</Badge>
                    <Badge variant={audVariant(viewRow.audience)}>{t(`communications.audience.${viewRow.audience}`)}</Badge>
                  </div>
                  <p className="panel__muted">
                    {viewRow.author} · {formatDate(viewRow.date)}
                  </p>
                  <p className="comm-view-summary">{viewRow.summary}</p>
                </>
              )}
            </div>
            <div className="modal__actions">
              <button type="button" className="btn btn--primary" onClick={() => setViewRow(null)}>
                {t('communications.viewClose')}
              </button>
            </div>
          </div>
        </div>
      )}

      {tplModal && (
        <div className="modal-backdrop" role="dialog" aria-modal>
          <div className="modal modal--wide">
            <h2 className="modal__title">
              {tplForm.isCustom && !tplForm.id ? t('communications.tplNew') : t('communications.tplEdit')}
            </h2>
            <div className="form-grid">
              <label className="field field--full">
                <span>{t('communications.tplName')}</span>
                <input
                  className="input"
                  value={tplForm.displayName}
                  onChange={(e) => setTplForm({ ...tplForm, displayName: e.target.value })}
                  placeholder={tplForm.key}
                />
              </label>
              <label className="field">
                <span>{t('communications.tplCategory')}</span>
                <select className="input" value={tplForm.category} onChange={(e) => setTplForm({ ...tplForm, category: e.target.value })}>
                  {TEMPLATE_EDIT_CATEGORIES.map((c) => (
                    <option key={c.id} value={c.id}>
                      {t(c.labelKey)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>{t('communications.tplChannel')}</span>
                <select className="input" value={tplForm.channel} onChange={(e) => setTplForm({ ...tplForm, channel: e.target.value })}>
                  <option value="email">{tplChannelLabel('email')}</option>
                  <option value="portal">{tplChannelLabel('portal')}</option>
                  <option value="internal">{tplChannelLabel('internal')}</option>
                </select>
              </label>
              <label className="field">
                <span>{t('communications.tplStatus')}</span>
                <select className="input" value={tplForm.status} onChange={(e) => setTplForm({ ...tplForm, status: e.target.value })}>
                  <option value="active">{t('communications.tplActive')}</option>
                  <option value="draft">{t('communications.tplDraft')}</option>
                </select>
              </label>
              <label className="field field--full">
                <span>{t('communications.tplVars')}</span>
                <input className="input" value={tplForm.vars} onChange={(e) => setTplForm({ ...tplForm, vars: e.target.value })} />
              </label>
              <label className="field field--full">
                <span>{t('communications.tplPreviewText')}</span>
                <textarea
                  className="input input--textarea"
                  rows={3}
                  value={tplForm.previewText}
                  onChange={(e) => setTplForm({ ...tplForm, previewText: e.target.value })}
                />
              </label>
            </div>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setTplModal(false)}>
                {t('communications.tplClose')}
              </button>
              <button type="button" className="btn btn--primary" onClick={saveTpl}>
                {t('communications.tplSave')}
              </button>
            </div>
          </div>
        </div>
      )}

      {tplPreviewRow && (
        <div className="modal-backdrop" role="dialog" aria-modal>
          <div className="modal">
            <h2 className="modal__title">{t('communications.tplPreview')}</h2>
            <p className="panel__muted text-sm">{tplTitleRow(tplPreviewRow)}</p>
            <p className="comm-template-preview" style={{ marginTop: '0.75rem' }}>
              {tplPreviewContent(tplPreviewRow) || '—'}
            </p>
            <div className="modal__actions">
              <button type="button" className="btn btn--primary" onClick={() => setTplPreviewRow(null)}>
                {t('communications.tplClose')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
